import React from 'react';
import { Button, Table } from '@odin-labs/components';
import { ChangeType } from 'apollo/generated/client-operations';
import { useModalChangeActions } from 'utils/useModalChangeActions';
import { PlusIcon } from 'components/icons';
import { NewHeader } from 'components/header/NewHeader';
import {
  DeleteGatewayModal,
  EditGatewayModal,
  EditableGateway,
  AddGatewayModal,
} from './modals';
import { GatewaysProps } from './types';
import { getColumns, GatewayColumn } from './Gateways.tables';

const classes = {
  error: 'odin-mt-2 odin-text-sm odin-text-odin-danger',
};

export const Gateways = React.forwardRef(
  (
    props: GatewaysProps,
    ref: React.ForwardedRef<HTMLDivElement>,
  ): React.ReactElement => {
    const { value, onChange, loading, error } = props;

    const {
      change,
      closeModal,
      openAddModal,
      openEditModal,
      openDeleteModal,
      updateValueAndCloseModal,
    } = useModalChangeActions<EditableGateway>({
      items: value,
      onChange,
      idField: 'id',
    });

    const columns = React.useMemo<GatewayColumn[]>(
      () => getColumns({ openEditModal, openDeleteModal }),
      [openEditModal, openDeleteModal],
    );

    const items = React.useMemo(() => {
      return value?.filter((item) => item.changeType !== ChangeType.Removed);
    }, [value]);

    const renderError = (): React.ReactNode => {
      if (!error) return null;
      if (typeof error === 'string') {
        return <p className={classes.error}>{error}</p>;
      }
      return <div className={classes.error}>{error}</div>;
    };

    const innerRef = React.useRef<HTMLDivElement>();

    React.useImperativeHandle(ref, () =>
      Object.assign(innerRef.current, {
        focus: () => {
          innerRef.current?.scrollIntoView({ behavior: 'smooth' });
        },
      }),
    );

    return (
      <>
        <div ref={innerRef} className="odin-flex odin-flex-col odin-gap-y-6">
          <NewHeader
            title="Gateways"
            size="md"
            actionsProps={{
              headerActions: null,
              children: (
                <Button
                  icon={PlusIcon}
                  text="Add Gateway"
                  size="sm"
                  onClick={openAddModal}
                />
              ),
            }}
          />
          <Table
            loading={loading}
            columns={columns}
            data={items}
            remote
            disableGlobalFilter
            disableSortBy
          />
        </div>
        {renderError()}
        <AddGatewayModal
          isOpen={change?.type === 'create'}
          onCancel={closeModal}
          onConfirm={updateValueAndCloseModal}
          gateways={value}
        />
        <EditGatewayModal
          isOpen={change?.type === 'update'}
          onCancel={closeModal}
          onConfirm={updateValueAndCloseModal}
          gateways={value}
          gateway={change?.item}
        />
        <DeleteGatewayModal
          isOpen={change?.type === 'remove'}
          onCancel={closeModal}
          onConfirm={updateValueAndCloseModal}
          gateway={change?.item}
        />
      </>
    );
  },
);
